import {
  MixedSchema,
  initVersionedSchema,
  toLinkContent,
  toParagraphContent,
  toTextContent,
} from '@principle-theorem/editor';
import {
  CollectionGroup,
  ITemplateDefinition,
  TemplateScope,
  TemplateType,
  IBrand,
  ITemplateDefinitionWithName,
  IResolvedTemplateWithName,
  SystemTemplate,
} from '@principle-theorem/principle-core/interfaces';
import {
  asyncForEach,
  doc,
  doc$,
  docOrDefault$,
  DocumentReference,
  getEnumValues,
  getParentDocRef,
  safeCombineLatest,
  snapshot,
  subCollection,
  WithRef,
  WithRefOrUnsaved,
} from '@principle-theorem/shared';
import { findKey, values } from 'lodash';
import { Observable } from 'rxjs';
import { TemplateDefinition } from './template-definition';

export interface ISystemTemplateWithUid {
  uid: string;
  template: ITemplateDefinition;
}

export type DepositSystemTemplate =
  | SystemTemplate.DepositRequestSMS
  | SystemTemplate.DepositRequestEmail
  | SystemTemplate.DepositConfirmationEmail
  | SystemTemplate.DepositReminderEmail
  | SystemTemplate.DepositForfeitNoticeEmail
  | SystemTemplate.DepositRefundConfirmationEmail;

export const SYSTEM_TEMPLATES_IDS: Record<SystemTemplate, string> = {
  [SystemTemplate.PrintHeader]: 'print-header',
  [SystemTemplate.PrintFooter]: 'print-footer',
  [SystemTemplate.PrintInvoiceHeader]: 'print-invoice-header',
  [SystemTemplate.PrintInvoiceFooter]: 'print-invoice-footer',
  [SystemTemplate.PrintTreatmentPlanHeader]: 'print-treatment-plan-header',
  [SystemTemplate.PrintTreatmentPlanFooter]: 'print-treatment-plan-footer',
  [SystemTemplate.InvoiceSMS]: 'invoice-sms',
  [SystemTemplate.InvoiceEmail]: 'invoice-email',
  [SystemTemplate.GapOfferSMS]: 'gap-offer-sms',
  [SystemTemplate.GapOfferEmail]: 'gap-offer-email',
  [SystemTemplate.DepositRequestSMS]: 'deposit-request-sms',
  [SystemTemplate.DepositRequestEmail]: 'deposit-request-email',
  [SystemTemplate.DepositConfirmationEmail]: 'deposit-confirmation-email',
  [SystemTemplate.DepositReminderEmail]: 'deposit-reminder-email',
  [SystemTemplate.DepositForfeitNoticeEmail]: 'deposit-forfeit-notice-email',
  [SystemTemplate.DepositRefundConfirmationEmail]:
    'deposit-refund-confirmation-email',
};

export const DEFAULT_SEND_INVOICE_SMS_CONTENT = initVersionedSchema([
  toParagraphContent([toTextContent('Hi {{ patientFirstName }},')]),
  toParagraphContent([
    toTextContent(
      'Please follow the link to view your invoice from {{ invoiceCreatedAt }} at {{ invoicePracticeName }}:'
    ),
  ]),
  toParagraphContent([toTextContent('{{ invoiceLink }}')]),
]);

export const DEFAULT_SEND_INVOICE_EMAIL_CONTENT = initVersionedSchema([
  toParagraphContent([toTextContent('Hi {{ patientFirstName }},')]),
  toParagraphContent([
    toTextContent(
      'Please follow the link to view your invoice from {{ invoiceCreatedAt }} at {{ invoicePracticeName }}:'
    ),
  ]),
  toParagraphContent([toLinkContent('View Invoice', '{{ invoiceLink }}')]),
]);

export const DEFAULT_GAP_OFFER_CONTENT = initVersionedSchema([
  toParagraphContent([toTextContent('Hi {{ patientFirstName }}, ')]),
  toParagraphContent([
    toTextContent(
      'You have been offered an earlier time slot for your next appointment at {{ practiceName }}, currently scheduled for {{ appointmentStartDate }}.'
    ),
  ]),
  toParagraphContent([
    toTextContent(
      'The new time slot available is for {{ gapOfferStartDate }}. If you would like to accept this new time slot, please click the link below. No action is required for you to keep your current appointment time.'
    ),
  ]),
  toParagraphContent([toTextContent('{{ gapOfferConfirmUrl }}')]),
]);

export const DEFAULT_ACC_PATIENT_FORMS_CONTENT = initVersionedSchema([
  toParagraphContent([toTextContent('Hi {{ patientFirstName }},')]),
  toParagraphContent([
    toTextContent('Please follow the link to complete your ACC forms'),
  ]),
  toParagraphContent([toTextContent('{{ patientFormsLink }}')]),
]);

export const DEFAULT_DEPOSIT_REQUEST_SMS_CONTENT = initVersionedSchema([
  toParagraphContent([toTextContent('Hi {{ patientFirstName }},')]),
  toParagraphContent([
    toTextContent(
      'A deposit of {{ depositAmount }} is required for your {{ treatmentName }} appointment scheduled for {{ appointmentDate }} at {{ appointmentTime }} with {{ practitionerName }} at {{ practiceName }}.'
    ),
  ]),
  toParagraphContent([
    toTextContent(
      'Please follow the link below to complete your deposit payment:'
    ),
  ]),
  toParagraphContent([toTextContent('{{ appointmentDepositPaymentLink }}')]),
]);

export const DEFAULT_DEPOSIT_REQUEST_EMAIL_CONTENT = initVersionedSchema([
  toParagraphContent([toTextContent('Hi {{ patientFirstName }},')]),
  toParagraphContent([
    toTextContent(
      'A deposit of {{ depositAmount }} is required for your {{ treatmentName }} appointment scheduled for {{ appointmentDate }} at {{ appointmentTime }} with {{ practitionerName }} at {{ practiceName }}.'
    ),
  ]),
  toParagraphContent([
    toTextContent(
      'Please follow the link below to complete your deposit payment:'
    ),
  ]),
  toParagraphContent([
    toLinkContent('Pay Deposit', '{{ appointmentDepositPaymentLink }}'),
  ]),
]);

export const DEFAULT_DEPOSIT_CONFIRMATION_EMAIL_CONTENT = initVersionedSchema([
  toParagraphContent([toTextContent('Hi {{ patientFirstName }},')]),
  toParagraphContent([
    toTextContent(
      'Thank you for your deposit payment of {{ depositAmount }} for your {{ treatmentName }} appointment scheduled for {{ appointmentDate }} at {{ appointmentTime }} with {{ practitionerName }} at {{ practiceName }}.'
    ),
  ]),
  toParagraphContent([
    toTextContent(
      'Your appointment is not confirmed yet. Our team will review your booking and be in touch to confirm your appointment time.'
    ),
  ]),
  toParagraphContent([
    toTextContent(
      'Please contact us if you have any questions or need to change your booking.'
    ),
  ]),
]);

export const DEFAULT_DEPOSIT_REMINDER_EMAIL_CONTENT = initVersionedSchema([
  toParagraphContent([toTextContent('Hi {{ patientFirstName }},')]),
  toParagraphContent([
    toTextContent(
      'This is a reminder that your {{ treatmentName }} appointment is scheduled for {{ appointmentDate }} at {{ appointmentTime }} with {{ practitionerName }} at {{ practiceName }}.'
    ),
  ]),
  toParagraphContent([
    toTextContent(
      'A deposit of {{ depositAmount }} is required to confirm your appointment.'
    ),
  ]),
  toParagraphContent([
    toTextContent(
      'Please follow the link below to complete your deposit payment:'
    ),
  ]),
  toParagraphContent([
    toLinkContent('Pay Deposit', '{{ appointmentDepositPaymentLink }}'),
  ]),
  toParagraphContent([
    toTextContent(
      'Please contact us if you have any questions or need to reschedule.'
    ),
  ]),
]);

export const DEFAULT_DEPOSIT_FORFEIT_NOTICE_EMAIL_CONTENT = initVersionedSchema(
  [
    toParagraphContent([toTextContent('Hi {{ patientFirstName }},')]),
    toParagraphContent([
      toTextContent(
        'We regret to inform you that your {{ treatmentName }} appointment scheduled for {{ appointmentDate }} at {{ appointmentTime }} with {{ practitionerName }} at {{ practiceName }} has been cancelled.'
      ),
    ]),
    toParagraphContent([
      toTextContent(
        'Your deposit of {{ depositAmount }} has been forfeited in accordance with our cancellation policy.'
      ),
    ]),
    toParagraphContent([
      toLinkContent('View Cancellation Policy', '{{ cancellationPolicyUrl }}'),
    ]),
  ]
);

export const DEFAULT_DEPOSIT_REFUND_CONFIRMATION_EMAIL_CONTENT =
  initVersionedSchema([
    toParagraphContent([toTextContent('Hi {{ patientFirstName }},')]),
    toParagraphContent([
      toTextContent(
        'Your deposit refund of {{ depositAmount }} for your cancelled {{ treatmentName }} appointment has been processed.'
      ),
    ]),
    toParagraphContent([
      toTextContent(
        'The refund should appear in your account within 3-5 business days.'
      ),
    ]),
    toParagraphContent([
      toTextContent(
        'If you would like to reschedule your appointment, please contact {{ practiceName }}.'
      ),
    ]),
  ]);

export const BRAND_ADD_DEFAULT_SYSTEM_TEMPLATES = [
  SystemTemplate.InvoiceSMS,
  SystemTemplate.InvoiceEmail,
  SystemTemplate.GapOfferSMS,
  SystemTemplate.GapOfferEmail,
];

const depositSystemTemplateRecord: Record<DepositSystemTemplate, true> = {
  [SystemTemplate.DepositRequestSMS]: true,
  [SystemTemplate.DepositRequestEmail]: true,
  [SystemTemplate.DepositConfirmationEmail]: true,
  [SystemTemplate.DepositReminderEmail]: true,
  [SystemTemplate.DepositForfeitNoticeEmail]: true,
  [SystemTemplate.DepositRefundConfirmationEmail]: true,
};

export const DEPOSIT_SYSTEM_TEMPLATES: DepositSystemTemplate[] = Object.keys(
  depositSystemTemplateRecord
) as DepositSystemTemplate[];

export const SYSTEM_TEMPLATES_DEFAULTS: Record<
  SystemTemplate,
  (ownerScope: DocumentReference) => ITemplateDefinition
> = {
  [SystemTemplate.PrintHeader]: (ownerScope) =>
    TemplateDefinition.init({
      name: 'Print Header',
      type: TemplateType.Html,
      scope: TemplateScope.None,
      ownerScope,
    }),
  [SystemTemplate.PrintFooter]: (ownerScope) =>
    TemplateDefinition.init({
      name: 'Print Footer',
      type: TemplateType.Html,
      scope: TemplateScope.None,
      ownerScope,
    }),
  [SystemTemplate.PrintInvoiceHeader]: (ownerScope) =>
    TemplateDefinition.init({
      name: 'Print Invoice Header',
      type: TemplateType.Html,
      scope: TemplateScope.Invoice,
      ownerScope,
    }),
  [SystemTemplate.PrintInvoiceFooter]: (ownerScope) =>
    TemplateDefinition.init({
      name: 'Print Invoice Footer',
      type: TemplateType.Html,
      scope: TemplateScope.Invoice,
      ownerScope,
    }),
  [SystemTemplate.PrintTreatmentPlanHeader]: (ownerScope) =>
    TemplateDefinition.init({
      name: 'Print Treatment Plan Header',
      type: TemplateType.Html,
      scope: TemplateScope.TreatmentPlan,
      ownerScope,
    }),
  [SystemTemplate.PrintTreatmentPlanFooter]: (ownerScope) =>
    TemplateDefinition.init({
      name: 'Print Treatment Plan Footer',
      type: TemplateType.Html,
      scope: TemplateScope.TreatmentPlan,
      ownerScope,
    }),
  [SystemTemplate.InvoiceSMS]: (ownerScope) =>
    TemplateDefinition.init({
      name: 'Send Invoice as SMS',
      type: TemplateType.PlainText,
      scope: TemplateScope.Invoice,
      content: DEFAULT_SEND_INVOICE_SMS_CONTENT,
      ownerScope,
    }),
  [SystemTemplate.InvoiceEmail]: (ownerScope) =>
    TemplateDefinition.init({
      name: 'Send Invoice as Email',
      type: TemplateType.Html,
      scope: TemplateScope.Invoice,
      content: DEFAULT_SEND_INVOICE_EMAIL_CONTENT,
      ownerScope,
    }),
  [SystemTemplate.GapOfferSMS]: (ownerScope) =>
    TemplateDefinition.init({
      name: 'Gap Offer SMS',
      type: TemplateType.PlainText,
      scope: TemplateScope.GapOffer,
      content: DEFAULT_GAP_OFFER_CONTENT,
      ownerScope,
    }),
  [SystemTemplate.GapOfferEmail]: (ownerScope) =>
    TemplateDefinition.init({
      name: 'Gap Offer Email',
      type: TemplateType.Html,
      scope: TemplateScope.GapOffer,
      content: DEFAULT_GAP_OFFER_CONTENT,
      ownerScope,
    }),
  [SystemTemplate.DepositRequestSMS]: (ownerScope) =>
    TemplateDefinition.init({
      name: 'Deposit Request SMS',
      type: TemplateType.PlainText,
      scope: TemplateScope.Appointment,
      content: DEFAULT_DEPOSIT_REQUEST_SMS_CONTENT,
      ownerScope,
    }),
  [SystemTemplate.DepositRequestEmail]: (ownerScope) =>
    TemplateDefinition.init({
      name: 'Deposit Request Email',
      type: TemplateType.Html,
      scope: TemplateScope.Appointment,
      content: DEFAULT_DEPOSIT_REQUEST_EMAIL_CONTENT,
      ownerScope,
    }),
  [SystemTemplate.DepositConfirmationEmail]: (ownerScope) =>
    TemplateDefinition.init({
      name: 'Deposit Confirmation Email',
      type: TemplateType.Html,
      scope: TemplateScope.Appointment,
      content: DEFAULT_DEPOSIT_CONFIRMATION_EMAIL_CONTENT,
      ownerScope,
    }),
  [SystemTemplate.DepositReminderEmail]: (ownerScope) =>
    TemplateDefinition.init({
      name: 'Deposit Reminder Email',
      type: TemplateType.Html,
      scope: TemplateScope.Appointment,
      content: DEFAULT_DEPOSIT_REMINDER_EMAIL_CONTENT,
      ownerScope,
    }),
  [SystemTemplate.DepositForfeitNoticeEmail]: (ownerScope) =>
    TemplateDefinition.init({
      name: 'Deposit Forfeit Notice Email',
      type: TemplateType.Html,
      scope: TemplateScope.Appointment,
      content: DEFAULT_DEPOSIT_FORFEIT_NOTICE_EMAIL_CONTENT,
      ownerScope,
    }),
  [SystemTemplate.DepositRefundConfirmationEmail]: (ownerScope) =>
    TemplateDefinition.init({
      name: 'Deposit Refund Confirmation Email',
      type: TemplateType.Html,
      scope: TemplateScope.Appointment,
      content: DEFAULT_DEPOSIT_REFUND_CONFIRMATION_EMAIL_CONTENT,
      ownerScope,
    }),
};

export class SystemTemplates {
  /**
   * Generic Functions
   */
  static isSystemDocUid(uid?: string): boolean {
    return uid ? values(SYSTEM_TEMPLATES_IDS).includes(uid) : false;
  }

  static getKeyFromUid(id: string): SystemTemplate | undefined {
    return findKey(SYSTEM_TEMPLATES_IDS, (item) => item === id) as
      | SystemTemplate
      | undefined;
  }

  static getUidFromKey(key: SystemTemplate): string {
    return SYSTEM_TEMPLATES_IDS[key];
  }

  static getResolverFromKey(
    key: SystemTemplate
  ): (ownerScope: DocumentReference) => ITemplateDefinition {
    return SYSTEM_TEMPLATES_DEFAULTS[key];
  }

  /**
   * System Template Specific
   */
  static getDefaultTemplate(
    systemTemplate: SystemTemplate,
    ownerScope: DocumentReference
  ): ITemplateDefinition {
    const resolver = SystemTemplates.getResolverFromKey(systemTemplate);
    return resolver(ownerScope);
  }

  static getSystemTemplatesWithUid(
    systemTemplates: SystemTemplate[],
    ownerScope: DocumentReference
  ): ISystemTemplateWithUid[] {
    return systemTemplates.map((templateType) => ({
      uid: SYSTEM_TEMPLATES_IDS[templateType],
      template: this.getDefaultTemplate(templateType, ownerScope),
    }));
  }

  static docRef(
    systemTemplate: SystemTemplate,
    ownerScope: DocumentReference
  ): DocumentReference<ITemplateDefinition> {
    return doc(
      subCollection<ITemplateDefinition>(ownerScope, CollectionGroup.Templates),
      SystemTemplates.getUidFromKey(systemTemplate)
    );
  }

  static resolveSystemTemplate$(
    systemTemplate: SystemTemplate,
    ownerScope: DocumentReference
  ): Observable<WithRefOrUnsaved<ITemplateDefinition>> {
    const ref = SystemTemplates.docRef(systemTemplate, ownerScope);
    return SystemTemplates.getSystemTemplate$(ref);
  }

  static resolveSystemTemplates$(
    ownerScope: DocumentReference
  ): Observable<WithRefOrUnsaved<ITemplateDefinition>[]> {
    return safeCombineLatest(
      getEnumValues(SystemTemplate).map((template) =>
        SystemTemplates.resolveSystemTemplate$(template, ownerScope)
      )
    );
  }

  static getSystemTemplate$(
    ref: DocumentReference<ITemplateDefinition>
  ): Observable<WithRefOrUnsaved<ITemplateDefinition>> {
    const template = SystemTemplates.getKeyFromUid(ref.id);
    if (!template) {
      return doc$(ref);
    }
    const defaultValue = SystemTemplates.getDefaultTemplate(
      template,
      getParentDocRef(ref)
    );
    return docOrDefault$(ref, defaultValue);
  }

  static async getSystemTemplate(
    template: SystemTemplate,
    brand: WithRef<IBrand>
  ): Promise<WithRefOrUnsaved<ITemplateDefinition>> {
    const templateRef = SystemTemplates.docRef(template, brand.ref);
    return snapshot(SystemTemplates.getSystemTemplate$(templateRef));
  }

  static async resolveSystemTemplates(
    brand: WithRef<IBrand>,
    templates: SystemTemplate[]
  ): Promise<ITemplateDefinitionWithName[]> {
    return asyncForEach(templates, async (template) => {
      const templateDoc = await SystemTemplates.getSystemTemplate(
        template,
        brand
      );
      return { templateDoc, template };
    });
  }

  static hasTemplateVariants(
    templates: ITemplateDefinitionWithName[]
  ): boolean {
    return templates.some((template) => template.templateDoc.variants?.length);
  }

  static findTemplate(
    templates: IResolvedTemplateWithName[],
    template: SystemTemplate
  ): MixedSchema | undefined {
    return templates.find(
      (printTemplate) => printTemplate.template === template
    )?.content;
  }
}
